// ShareBite notification ordering & push-failure isolation (Task 9.5).
//
// Two pure, platform-independent functions backing the Notification Center,
// which is the authoritative record of notification events (FCM push is a
// best-effort, derived side effect):
//
//  1. Ordering — the Notification Center presents entries strictly from newest
//     to oldest by creation timestamp (Req 19.1).
//
//  2. Push-failure isolation — when a Push Notification delivery fails, the
//     corresponding Notification Center entry remains present and marked unread,
//     and the recorded event is otherwise unaffected (Req 19.9).
//
// The functions perform no I/O and have no side effects; they never mutate
// their inputs.
//
// Design reference: design.md "Property 21: Notification ordering and
// push-failure isolation".
// Requirements: 19.1, 19.9.
/**
 * Order Notification Center entries newest-to-oldest by creation timestamp
 * (Req 19.1).
 *
 * Returns a new array sorted by `createdAt` descending. Ties (equal
 * timestamps) preserve the input relative order (stable sort). Pure: the input
 * array is copied before sorting, so the caller's array is never mutated.
 *
 * @param notifications The Notification Center entries in any order.
 */
export function orderNotifications(notifications) {
    return [...notifications].sort((a, b) => b.createdAt - a.createdAt);
}
/**
 * Apply the effect of a failed Push Notification delivery to its Notification
 * Center entry (Req 19.9).
 *
 * Returns a new {@link Notification} that is identical to `entry` except it is
 * guaranteed to remain present and marked unread (`read: false`). The recorded
 * event — its type, recipient, related entity, and creation timestamp — is
 * unaffected. A push-delivery failure therefore never removes or alters the
 * authoritative Notification Center entry beyond keeping it unread.
 *
 * Pure: returns a fresh object and never mutates `entry`.
 *
 * @param entry The Notification Center entry whose push delivery failed.
 */
export function onPushDeliveryFailure(entry) {
    return { ...entry, read: false };
}
//# sourceMappingURL=notifications.js.map